import { useState, useEffect, useRef, useCallback } from 'react';
import {
  GraduationCap, Building2, Plane, Shield, Home, Users,
  Sparkles, ChevronRight, X, Search, Compass
} from 'lucide-react';

const TOUR_STEPS = [
  {
    target: '[data-testid="bottom-nav-explore"]',
    fallbackTarget: '[data-testid="sidebar-quick-nav"]',
    title: 'Explore',
    description: 'Browse colleges, courses, and services — your one-stop shop for studying abroad.',
    icon: Compass,
    color: 'from-blue-500 to-indigo-500',
    position: 'top',
  },
  {
    target: '[data-testid="bottom-nav-feed"]',
    fallbackTarget: '[data-testid="sidebar-quick-nav"]',
    title: 'Community Feed',
    description: 'Stay connected with fellow students. Share experiences, ask questions, get answers.',
    icon: Users,
    color: 'from-emerald-500 to-teal-500',
    position: 'top',
  },
  {
    target: '[data-testid="bottom-nav-ai"]',
    fallbackTarget: '[data-testid="sidebar-ai-box"]',
    title: 'Unexa AI',
    description: 'Your personal AI advisor for visa roadmaps, application guidance, and migration planning.',
    icon: Sparkles,
    color: 'from-violet-500 to-purple-500',
    position: 'top',
  },
  {
    target: '[data-testid="bottom-nav-dashboard"]',
    fallbackTarget: '[data-testid="sidebar-quick-nav"]',
    title: 'My Dashboard',
    description: 'Track your applications, deadlines, and documents — all in one place.',
    icon: GraduationCap,
    color: 'from-amber-500 to-orange-500',
    position: 'top',
  },
  {
    target: '[data-testid="explore-ai-bubble"], [data-testid="explore-ai-panel"]',
    fallbackTarget: '[data-testid="sidebar-explore-services"]',
    title: 'Quick Services',
    description: 'Find colleges, courses, visa services, insurance, and more — everything you need in one place.',
    icon: Search,
    color: 'from-fuchsia-500 to-pink-500',
    position: 'top',
    isFloating: true,
  },
];

function TourTooltip({ step, stepIndex, totalSteps, onNext, onSkip, targetRect }) {
  const Icon = step.icon;
  const isLast = stepIndex === totalSteps - 1;

  const style = {};
  if (targetRect && !step.isFloating) {
    style.position = 'fixed';
    style.zIndex = 200;
    // If target is in left sidebar (x < 300), position tooltip to the right
    if (targetRect.left < 300 && targetRect.top < window.innerHeight - 200) {
      style.top = `${targetRect.top}px`;
      style.left = `${targetRect.right + 16}px`;
    } else {
      // Bottom nav or other — position above
      style.bottom = `${window.innerHeight - targetRect.top + 12}px`;
      style.left = `${Math.max(16, Math.min(targetRect.left + targetRect.width / 2 - 160, window.innerWidth - 336))}px`;
    }
  } else {
    style.position = 'fixed';
    style.top = '50%';
    style.left = '50%';
    style.transform = 'translate(-50%, -50%)';
  }

  return (
    <div
      className="z-[200] w-[320px] animate-tour-in"
      style={style}
      data-testid={`tour-tooltip-${stepIndex}`}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
        {/* Colored header strip */}
        <div className={`h-1.5 bg-gradient-to-r ${step.color}`} />

        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shrink-0`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-slate-800 text-sm">{step.title}</h4>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{step.description}</p>
            </div>
          </div>

          {/* Progress + actions */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i <= stepIndex ? `w-4 bg-gradient-to-r ${step.color}` : 'w-1.5 bg-slate-200'
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onSkip}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="tour-skip"
              >
                Skip
              </button>
              <button
                onClick={onNext}
                className={`px-4 py-1.5 rounded-full bg-gradient-to-r ${step.color} text-white text-xs font-semibold flex items-center gap-1 hover:shadow-md transition-all`}
                data-testid="tour-next"
              >
                {isLast ? "Let's Go!" : 'Next'}
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Arrow pointer */}
      {targetRect && !step.isFloating && (
        <div
          className="w-3 h-3 bg-white border-b border-r border-slate-100 rotate-45 mx-auto -mt-1.5"
          style={{ marginLeft: targetRect ? `${targetRect.left + targetRect.width / 2 - Math.max(16, Math.min(targetRect.left + targetRect.width / 2 - 160, window.innerWidth - 336)) - 6}px` : 'auto' }}
        />
      )}
    </div>
  );
}

export default function FeatureTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);

  // Delay start to ensure DOM is ready
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const updateTargetRect = useCallback(() => {
    const step = TOUR_STEPS[currentStep];
    if (!step) return;

    let el = document.querySelector(step.target);
    if (!el && step.fallbackTarget) {
      el = document.querySelector(step.fallbackTarget);
    }

    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  useEffect(() => {
    updateTargetRect();
    window.addEventListener('resize', updateTargetRect);
    return () => window.removeEventListener('resize', updateTargetRect);
  }, [updateTargetRect]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTour();
    }
  };

  // Auto-skip steps where the target doesn't exist
  useEffect(() => {
    if (!visible) return;
    const step = TOUR_STEPS[currentStep];
    if (!step) return;
    // Small delay to let DOM render
    const timer = setTimeout(() => {
      let el = document.querySelector(step.target);
      if (!el && step.fallbackTarget) {
        el = document.querySelector(step.fallbackTarget);
      }
      if (!el && !step.isFloating) {
        if (currentStep < TOUR_STEPS.length - 1) {
          setCurrentStep(prev => prev + 1);
        } else {
          completeTour();
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [currentStep, visible]);

  const completeTour = () => {
    setVisible(false);
    localStorage.setItem('unexa_tour_complete', 'true');
    onComplete?.();
  };

  if (!visible) return null;

  const step = TOUR_STEPS[currentStep];

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[190] bg-black/50 transition-opacity" data-testid="tour-overlay">
        {/* Spotlight cutout on target */}
        {targetRect && !step.isFloating && (
          <div
            className="absolute rounded-xl ring-4 ring-white/30 bg-transparent"
            style={{
              top: targetRect.top - 4,
              left: targetRect.left - 4,
              width: targetRect.width + 8,
              height: targetRect.height + 8,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
              zIndex: 191,
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <TourTooltip
        step={step}
        stepIndex={currentStep}
        totalSteps={TOUR_STEPS.length}
        onNext={handleNext}
        onSkip={completeTour}
        targetRect={targetRect}
      />

      <style>{`
        @keyframes tourIn {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-tour-in { animation: tourIn 0.3s ease-out; }
      `}</style>
    </>
  );
}
